import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { SearchIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { KEY_CODES } from 'constants/keyboard';
import useAutofocusSearchOnOpen from 'hooks/useAutofocusSearchOnOpen';

import { filterNavItems, flattenNavItems } from '../../BottomNav.utils';

import {
  emptyStateStyles,
  overlayStyles,
  paletteCardStyles,
  resultIconStyles,
  resultItemStyles,
  resultsListStyles,
  resultTitleWrapperStyles,
  searchIconStyles,
  searchInputStyles,
  searchRowStyles,
  sectionLabelStyles,
} from './CommandPalette.styles';
import { CommandPaletteTestIds } from './CommandPalette.testIds';

/**
 * Single search result row in the command palette.
 */
const PaletteResultItem = memo(({ item, onSelect }) => {
  const IconComponent = item.icon;

  const onPress = useCallback(() => {
    onSelect(item);
  }, [item, onSelect]);

  const onKeyDown = useCallback(
    e => {
      if (e.keyCode === KEY_CODES.ENTER) {
        onSelect(item);
      }
    },
    [item, onSelect],
  );

  return (
    <Box
      component="button"
      data-testid={CommandPaletteTestIds.getResult(item.id)}
      sx={resultItemStyles}
      onClick={onPress}
      onKeyDown={onKeyDown}
    >
      <Box sx={resultIconStyles}>
        {IconComponent ? <IconComponent size={IconSize[20]} /> : <SearchIcon size={IconSize[20]} />}
      </Box>
      <Box sx={resultTitleWrapperStyles}>
        <Typography noWrap variant="regular16">
          {item.title}
        </Typography>
      </Box>
    </Box>
  );
});

PaletteResultItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
    icon: PropTypes.elementType,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

/**
 * Full-screen command palette for destination search.
 * Filters all permitted nav destinations by title.
 * Tapping / pressing Enter navigates and closes.
 *
 * Future: add record/loan search below the destination section.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Array} props.navItems - top-level nav items from selectNavigationConfig
 */
const CommandPalette = ({ isOpen, onClose, navItems }) => {
  const history = useHistory();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');

  const allDestinations = useMemo(() => flattenNavItems(navItems), [navItems]);

  const results = useMemo(() => filterNavItems(allDestinations, query), [allDestinations, query]);

  const onQueryChange = useCallback(e => {
    setQuery(e.target.value);
  }, []);

  const onSelect = useCallback(
    item => {
      if (item.url) {
        history.push(item.url);
      }

      setQuery('');
      onClose();
    },
    [history, onClose],
  );

  const onOverlayKeyDown = useCallback(
    e => {
      if (e.keyCode === KEY_CODES.ESC) {
        setQuery('');
        onClose();
      }
    },
    [onClose],
  );

  const onOverlayClick = useCallback(
    e => {
      if (e.target === e.currentTarget) {
        setQuery('');
        onClose();
      }
    },
    [onClose],
  );

  useAutofocusSearchOnOpen({ isOpen, inputRef });

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      aria-label="Command palette"
      data-testid={CommandPaletteTestIds.ROOT}
      role="dialog"
      sx={overlayStyles}
      onClick={onOverlayClick}
      onKeyDown={onOverlayKeyDown}
    >
      <Box sx={paletteCardStyles}>
        <Box sx={searchRowStyles}>
          <Box sx={searchIconStyles}>
            <SearchIcon size={IconSize[20]} />
          </Box>
          <Box
            component="input"
            placeholder="Search destinations…"
            ref={inputRef}
            sx={searchInputStyles}
            type="text"
            value={query}
            onChange={onQueryChange}
          />
        </Box>
        <Box sx={resultsListStyles}>
          {results.length === 0 ? (
            <Box sx={emptyStateStyles}>
              <Typography color="text.secondary" variant="regular14">
                No destinations found
              </Typography>
            </Box>
          ) : (
            <>
              <Typography color="text.secondary" sx={sectionLabelStyles} variant="medium12">
                Destinations
              </Typography>
              {results.map(item => (
                <PaletteResultItem item={item} key={item.id} onSelect={onSelect} />
              ))}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

CommandPalette.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  navItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(CommandPalette);
